<template>
    <div>
        <card-header title="Enrollment Referral Report" icon="fa-clipboard-check"/>

        <div class="custom-toolbar" v-if="!review">
            <div class="columns">
                <div class="column is-4">
                    <b-field label="Start Date" label-position="inside">
                        <b-datepicker append-to-body v-model="startDate" :min-date="minDate" @input="changeDate"/>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="End Date" label-position="inside">
                        <b-datepicker append-to-body v-model="endDate" :min-date="minDate" @input="changeDate"/>
                    </b-field>
                </div>
                <div class="column">
                    <b-field>
                        <b-switch size="is-small" v-model="hideCompleted">Hide Completed</b-switch>
                    </b-field>
                    <b-field>
                        <b-switch size="is-small" v-model="useEnrollment" @input="changeDate">Use Enrollment Date instead of Defer Date</b-switch>
                    </b-field>
                </div>
            </div>
            <div v-if="!currentUser.isSuper && currentUser.id === 37">
                <b-field label="Reseller" label-position="inside">
                    <b-select expanded v-model="selectedResellerId">
                        <option value="2591">John Twining</option>
                        <option value="14037">Chad Mathinson</option>
                        <option value="22455">Robert Collins</option>
                        <option value="26366">Gus Montgomery</option>
                    </b-select>
                </b-field>
            </div>
            <card-list class="is-align-content-start">
                <search-list-item @action="searchClient" class="is-align-content-start" />
            </card-list>
        </div>

        <card-body>
            <card-list>
                <reseller-referral-tile
                    :referral="r"
                    :active="r.toggle !== r.paid"
                    v-for="r in filteredReferrals"
                />
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3" v-if="!review">

            <div class="columns">
                <div class="column" v-if="currentUser.isSuper">
                    <b-button
                        type="is-info"
                        size="is-small"
                        class="mr-3"
                        :disabled="!changed"
                        @click="reviewChanges()"
                    ><i class="fas fa-check mr-2"></i>Review Changes
                    </b-button>
                </div>
                <div class="column">
                    <div class="is-size-7" v-if="currentUser.isSuper">Gross Total <strong>{{ total| currency }}</strong></div>
                    <div class="is-size-7">{{ currentUser.isSuper ? 'Reseller' : 'Total' }} Paid <strong>{{ totalPaid | currency }}</strong></div>
                    <div class="is-size-7">{{ currentUser.isSuper ? 'Reseller' : 'Total' }} Unpaid <strong>{{ totalUnpaid | currency }}</strong></div>
                </div>
                <div class="column" v-if="!currentUser.isSuper">
                    <div class="is-size-7">End of year <strong>{{ midPayout | currency }}</strong></div>
                    <div class="is-size-7">Contract end <strong>{{ completePayout | currency }}</strong></div>
                    <div class="is-size-7">December bonus <strong>{{ bonusPayout | currency }}</strong></div>
                </div>
                <div class="column has-text-right">
                    <div class="is-size-6" v-if="currentUser.isSuper"><strong>{{ toggleDelta | currency }}</strong></div>
                    <div class="is-size-6" v-else>Enrollment <strong>{{ enrollPayout | currency }}</strong></div>
                    <div><strong>{{ filteredReferrals.length }}</strong> of <strong>{{ referrals.length }}</strong></div>
                </div>
            </div>
        </card-footer>

        <card-footer class="pt-3 pb-3" v-else>

            <div class="columns">
                <div class="column">
                    <div class="is-size-4"><strong>{{ toggleDelta | currency }}</strong></div>
                </div>
                <div class="column has-text-right">
                    <b-button outlined size="is-small" @click="review=false" class="mr-3">Cancel</b-button>
                    <b-button
                        type="is-danger"
                        size="is-small"
                        class="mr-3"
                        :disabled="!changed"
                        @click="process()"
                    ><i class="fas fa-check mr-2"></i>Process
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import SearchListItem from "@/TIER/components/SearchListItem";

    import {client as http} from '@/http_client';
    import { mapGetters } from "vuex";

    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import CardToolbar from "../../TIER/components/CardToolbar";
    import moment from 'moment';
    import CardFooter from "../../TIER/components/CardFooter";
    import ResellerReferralTile from "../../components/tiles/ResellerReferralTile";

    export default {
        components: {ResellerReferralTile, CardFooter, CardToolbar, ContainerListItem, CardHeader, CardBody, CardList, SubheaderListItem, SearchListItem},
        props: ['card', 'props', 'child'],

        data() {
            return {
                referrals: [],
                reseller: null,
                selectedResellerId: null,
                startDate: moment().set('date', 1).toDate(),
                endDate: new Date(),
                hideCompleted: true,
                useEnrollment: false,
                review: false,
                searchQuery: '',
                minDate: new Date(new Date().setYear(new Date().getFullYear() - 3)),
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            filteredReferrals() {
                return this.referrals.filter(a => {
                    if (this.searchQuery.trim().length && (
                        (!a.firstname || (a.firstname && !a.firstname.toLowerCase().includes(this.searchQuery.toLowerCase()))) &&
                        (!a.lastname || (a.lastname && !a.lastname.toLowerCase().includes(this.searchQuery.toLowerCase()))) &&
                        (!a.id || (a.id && !a.id.toString().includes(this.searchQuery.toLowerCase())))
                        ))
                        return false;
                    if (this.review)
                        return a.toggle !== a.paid;
                    if (this.hideCompleted)
                        return !a.paid;
                    return true;
                });
            },
            toggleDelta() {
                let result = 0;
                for (let i = 0; i < this.referrals.length; i++) {
                    const r = this.referrals[i];
                    if (r.toggle !== r.paid) {
                        if (r.toggle) {
                            result += r.calc;
                        } else {
                            result -= r.calc;
                        }
                    }
                }
                return result;
            },
            totalPaid() {
                let result = 0;
                for (let i = 0; i < this.referrals.length; i++) {
                    const r = this.referrals[i];
                    if (r.paid) {
                        result += r.calc;
                    }
                }
                return result;
            },
            totalUnpaid() {
                let result = 0;
                for (let i = 0; i < this.referrals.length; i++) {
                    const r = this.referrals[i];
                    if (!r.paid && r.contractFax) {
                        result += r.calc;
                    }
                }
                return result;
            },
            total() {
                let result = 0;
                for (let i = 0; i < this.referrals.length; i++) {
                    if (this.referrals[i].contractFax) {
                        result += this.referrals[i].total;
                    }
                }
                return result;
            },
            enrollPayout() {
                let result = 0;
                for (let referral of this.referrals) {
                    if (referral.paid !== 1 && referral.contractFax && referral.type === 'enroll') {
                        result += referral.calc;
                    }
                }
                return result;
            },
            midPayout() {
                let result = 0;
                for (let referral of this.referrals) {
                    if (referral.paid !== 1 && referral.contractFax && referral.type === 'mid') {
                        result += referral.calc;
                    }
                }
                return result;
            },
            completePayout() {
                let result = 0;
                for (let referral of this.referrals) {
                    if (referral.paid !== 1 && referral.contractFax && referral.type === 'complete') {
                        result += referral.calc;
                    }
                }
                return result;
            },
            bonusPayout() {
                let result = 0;
                for (let referral of this.referrals) {
                    if (referral.paid !== 1 && referral.contractFax && referral.type === 'bonus') {
                        result += referral.calc;
                    }
                }
                return result;
            },
            changed() {
                for (let i = 0; i < this.referrals.length; i++) {
                    const r = this.referrals[i];
                    if (r.toggle !== r.paid) {
                        return true;
                    }
                }
                return false;
            }
        },
        methods: {
            loadReport: function(forced) {
                this.$emit('loading', true);
                this.referrals = [];
                this.reseller = null;

                http.post('resellers/referrals', {id: this.selectedResellerId, start: this.startDate, end: this.endDate, field: (this.useEnrollment) ? 'enroll' : 'defer'}, {force: forced}).then(response => {
                    this.referrals = response.data.referrals;
                    this.reseller = response.data.reseller;
                    this.$emit('loading', false);
                });
            },
            changeDate: function() {
                this.loadReport(true);
            },
            reviewChanges: function() {
                this.review = true;
            },
            process: function() {
                this.$emit('loading', true);
                let referrals = [];
                for (let i = 0; i < this.referrals.length; i++) {
                    const r = this.referrals[i];
                    if (r.toggle !== r.paid) referrals.push({
                        id: r.id,
                        firstName: r.firstname,
                        lastName: r.lastname,
                        date: moment(r.enrollDate).format('MMM Do, YYYY'),
                        type: r.type,
                        calc: (r.toggle) ? r.calc : r.calc*-1,
                        paid: r.toggle
                    });
                }

                let payload = {
                    reseller: {
                        firstName: this.reseller.first_name,
                        lastName: this.reseller.last_name
                    },
                    referrals
                };

                http.post('resellers/referrals/process', payload, {force: true}).then(response => {
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                });
            },
            searchClient: function(query) {
                this.searchQuery = query;
            }
        },
        watch: {
            selectedResellerId: function() {
                this.loadReport();
            }
        },
        mounted: function() {
            if (!this.props.userId && !this.currentUser.isSuper) {
                this.props.userId = this.currentUser.my247ed_id;
                this.startDate = moment().set('date', 1).set('month', 0).toDate();
                this.endDate = moment().set('month', 11).set('date', 31).toDate();
            }
            this.selectedResellerId = this.props.userId;
            if (this.currentUser.isSuper)
                this.$saveAlert(true, 'Are you done editing this report? Unsaved changes will be lost.');
        }
    };
</script>

<style lang="scss" scoped>
    .custom-toolbar {
        background: #f3f3f3;
        border-bottom: 1px solid #dee2e6;

        padding: 10px 0.4rem 10px 0.6rem;
    }
</style>
